import { useRef, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from "./context/AuthProvider";
import axiosInstance from './api/axios';
import MainMenu from "./MainMenu";
import Copyright from './Copyright';
import BackgroundImage from './images/DATAEPattern.png';
import { SOFTDATANODEJS } from './config.js';
import MainMenuHeader from './MainMenuHeader';

const hashSHA512 = async (text) => {
    const msgUint8 = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-512', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toLowerCase();
};

const Login = () => {
    const { setAuth } = useContext(AuthContext);

    const userRef = useRef();
    const errRef = useRef();

    const [user, setUser] = useState('');
    const [pwd, setPwd] = useState('');
    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);

    // ==========================================
    // ESTADOS PARA MODAL E LOGIN EM ESPERA
    // ==========================================
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [pendingAuth, setPendingAuth] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionExpired = params.get('sessionExpired') === '1';

        if (sessionExpired) {
            setErrMsg('A sua sessão expirou. Por favor, volte a iniciar sessão.');
        }

        if (userRef.current) {
            userRef.current.focus();
        }
    }, []);

    useEffect(() => {
        setErrMsg('');
    }, [user, pwd]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // PASSO 1: Autenticação Java
            let javaResponse;

            try {
                javaResponse = await axiosInstance.post("/user/validateUser", {
                    username: user,
                    password: pwd
                });
            } catch (err) {
                const hashedPwd = await hashSHA512(pwd);

                javaResponse = await axiosInstance.post("/user/validateUser", {
                    username: user,
                    password: hashedPwd
                });
            }

            if (javaResponse?.data?.status !== "OK") {
                setErrMsg('Falha na autenticação: Credenciais rejeitadas pelo sistema.');

                if (errRef.current) {
                    errRef.current.focus();
                }

                return;
            }

            const token = javaResponse.data.token;

            // PASSO 2: Obter Permissões Node.js
            let finalReportUser = false;
            let finalTablesUser = false;
            let finalName = user;
            let finalProfileName = "USER";

            const isAdmin = user.toLowerCase().trim() === "admin";
            let hasNodeWarning = false;

            try {
                const perfisRes = await axios.get(`${SOFTDATANODEJS}/mongodb/perfis`);
                const perfis = perfisRes.data;

                const currentUser = perfis.find(
                    p => p.login.toLowerCase() === user.toLowerCase()
                );

                if (currentUser) {
                    finalReportUser = currentUser.estado === 1;
                    finalTablesUser = !!currentUser._id;
                    finalName = currentUser.nome || finalName;
                    finalProfileName = currentUser.perfil === 1 ? "ADMIN" : "USER";
                } else if (isAdmin) {
                    finalReportUser = true;
                    finalTablesUser = true;
                }

            } catch (nodeErr) {
                // SE O NODE FALHAR: ativamos o aviso e permissões básicas
                setWarningMessage(
                    "Aviso: Falha ao ler permissões adicionais do servidor. A entrar com permissões básicas (Report)."
                );

                hasNodeWarning = true;
                finalReportUser = true;
            }

            // Preparar objeto de autenticação
            const authData = {
                token: token,
                user,
                name: finalName,
                profileName: finalProfileName,
                report: finalReportUser,
                tables: finalTablesUser,
                reportUser: finalReportUser,
                tablesUser: finalTablesUser,
                isAdmin
            };

            // =========================================================
            // LÓGICA DE DECISÃO: ENTRAR OU MOSTRAR MODAL
            // =========================================================
            if (hasNodeWarning) {
                // Se houver aviso, não chamamos o setAuth ainda
                setPendingAuth(authData);
                setShowWarningModal(true);
            } else {
                // Se estiver tudo OK, entra direto
                setAuth(authData);
                setPwd('');
                setSuccess(true);
            }

        } catch (err) {
            setErrMsg(
                err.response?.data?.error ||
                err.response?.data ||
                'Falha em comunicar com o servidor de autenticação.'
            );

            if (errRef.current) {
                errRef.current.focus();
            }
        }
    };

    return (
        <>
            {success ? (
                <MainMenu />
            ) : (
                <main className='App' style={{ backgroundImage: `url(${BackgroundImage})` }}>
                    <section>
                        <p
                            ref={errRef}
                            className={errMsg ? "errmsg" : "offscreen"}
                            aria-live="assertive"
                        >
                            {errMsg}
                        </p>

                        <MainMenuHeader />

                        <form onSubmit={handleSubmit}>
                            <label htmlFor="username">Username:</label>
                            <input
                                type="text"
                                id="username"
                                ref={userRef}
                                autoComplete="off"
                                onChange={(e) => setUser(e.target.value)}
                                value={user}
                                required
                            />

                            <label htmlFor="password">Password:</label>
                            <input
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                onChange={(e) => setPwd(e.target.value)}
                                value={pwd}
                                required
                            />

                            <br />

                            <center>
                                <button style={{ width: "50%" }}>
                                    Entrar
                                </button>
                            </center>
                        </form>

                        <br />
                        <Copyright />
                    </section>
                </main>
            )}

            {/* ========================================================= */}
            {/* MODAL DE AVISO: NODEJS OFFLINE                           */}
            {/* ========================================================= */}
            {showWarningModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 3000
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            width: '90%',
                            maxWidth: '400px',
                            borderRadius: '5px',
                            padding: '20px',
                            textAlign: 'center',
                            boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                        }}
                    >
                        <div style={{ color: '#E67E22', fontSize: '16px' }}>
                            <p style={{ fontSize: '40px', margin: '0 0 10px 0' }}>
                                ⚠️
                            </p>

                            <strong style={{ color: '#333' }}>
                                Aviso de Ligação
                            </strong>

                            <p
                                style={{
                                    color: '#333',
                                    marginTop: '10px',
                                    fontSize: '14px'
                                }}
                            >
                                {warningMessage}
                            </p>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: '20px'
                            }}
                        >
                            <button
                                style={{
                                    padding: '8px 25px',
                                    cursor: 'pointer',
                                    borderRadius: '3px',
                                    border: 'none',
                                    background: '#E67E22',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                                onClick={() => {
                                    setAuth(pendingAuth);
                                    setPwd('');
                                    setShowWarningModal(false);
                                    setSuccess(true);
                                }}
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Login;